/**
 * Große Zahleneingabe ohne Kasten — Ziffern in Akzentschrift über einem
 * lilafarbenen Unterstrich, Platzhalter als Zeichen-Schablone („JJJJ").
 *
 * Bewusst **kein** `type="number"`: dessen Pfeilchen sind auf Touch nicht zu
 * treffen, das Mausrad verstellt beim Scrollen unbemerkt den Wert, und der
 * Browser lässt dort Zeichen wie „e", „+" und „-" zu, die in einem Baujahr
 * nichts verloren haben. Stattdessen ein Textfeld mit Ziffernfilter —
 * `inputMode` bringt auf dem Handy trotzdem den Zifferblock.
 *
 * Gefiltert wird in `onChange`, nicht per `onKeyDown`: das Ereignis feuert auch
 * beim Einfügen aus der Zwischenablage, beim Ziehen von Text und bei der
 * Autovervollständigung — ein Tastenfilter ließe genau diese Wege offen.
 *
 * Der **eingehende** Wert wird nie umgeschrieben, nur die Nutzereingabe.
 * Bestandswerte (auch krumme Alt-Einträge) bleiben stehen, bis jemand sie
 * anfasst; dieselbe Linie wie das „kein stilles Überschreiben" im
 * `GesamtflaecheBlock`.
 */
import { makeStyles, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";

/**
 * Ziffernfilter für Tipp- und Einfüge-Eingaben.
 *
 * - ohne `dezimal`: ausschließlich Ziffern („19a8 5" → „1985"). Diesen Weg
 *   nehmen Baujahr und Geschosszahlen — dort ist jedes Trennzeichen ein Fehler.
 * - mit `dezimal`: zusätzlich Trennzeichen („," und "."), nur nicht zwei direkt
 *   hintereinander. Diesen Weg nimmt allein die Gesamtfläche.
 * - `maxLen` begrenzt die Zeichenzahl (bei `dezimal` inklusive Trennzeichen).
 *
 * **Warum der Dezimalmodus Trennzeichen nicht zählt:** ein Filter, der einen
 * Trenner verwirft, dessen Ziffern aber weiterlaufen lässt, verschiebt das
 * Komma und ändert damit still den Betrag. Eine deutsch geschriebene Fläche
 * „1.234,56" wurde so zu „1.23456" — aus 1.234,56 m² wurden 1,23 m² in der
 * Kundenakte, ohne dass jemand etwas davon sah. Dasselbe galt für einen
 * führenden Trenner („,5" → „5", also 0,5 → 5).
 *
 * Regel daher: **keine Ziffer wechselt je die Seite eines Trenners.** Erlaubt
 * sind beide Schreibweisen unverändert — „120,5", „1.234,56" (deutsch) und
 * „1,234.56" (englisch, kommt aus kopierten Tabellen) —, denn genau so stand es
 * vorher im Freitextfeld, und Vertragsmaske wie Zusammenfassung geben den Wert
 * unverändert aus. Herausgeworfen werden nur Zeichen, die keine Zahl sein
 * können: Buchstaben, Leerzeichen, Vorzeichen, Einheiten („120,5 m²").
 */
export const ziffernFilter = (
  roh: string,
  opt: { dezimal?: boolean; maxLen?: number } = {},
): string => {
  let gefiltert = "";
  for (const zeichen of roh) {
    if (zeichen >= "0" && zeichen <= "9") {
      gefiltert += zeichen;
      continue;
    }
    if (!opt.dezimal || (zeichen !== "," && zeichen !== ".")) continue;
    // Zwei Trenner direkt hintereinander („12,,5") ergäben keine Zahl — der
    // zweite fällt weg. Das gruppiert nichts um: davor wie danach steht
    // dieselbe Ziffernfolge um denselben Trenner.
    if (gefiltert.endsWith(",") || gefiltert.endsWith(".")) continue;
    gefiltert += zeichen;
  }
  return opt.maxLen != null ? gefiltert.slice(0, opt.maxLen) : gefiltert;
};

const useStyles = makeStyles({
  zeile: {
    display: "flex",
    alignItems: "baseline",
    columnGap: "10px",
  },
  eingabe: {
    width: "100%",
    maxWidth: "220px",
    boxSizing: "border-box",
    backgroundColor: "transparent",
    backgroundImage: "none",
    fontFamily: BRAND.fontAccent,
    fontSize: "2rem",
    lineHeight: 1.15,
    color: BRAND.ink,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: "2px",
    paddingBottom: "6px",
    // Longhands statt `shorthands.border`: nur die untere Kante trägt eine
    // Linie, die drei anderen sollen gar keine haben.
    borderTopStyle: "none",
    borderRightStyle: "none",
    borderLeftStyle: "none",
    borderBottomStyle: "solid",
    borderBottomWidth: "2px",
    borderBottomColor: BRAND.purple,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    outlineStyle: "none",
    "@media (min-width: 768px)": {
      fontSize: "2.4rem",
    },
    // Der Platzhalter ist die Schablone („JJJJ") und muss als Vorschlag lesbar
    // bleiben — gedämpft, aber nicht durchsichtig (Firefox setzt sonst 0.54).
    "::placeholder": {
      color: BRAND.muted,
      opacity: 1,
    },
    // Kräftigerer Unterstrich beim Schreiben; das Padding gibt den einen Pixel
    // wieder her, damit die Zahl nicht springt.
    ":focus": {
      borderBottomWidth: "3px",
      paddingBottom: "5px",
    },
    // Ohne Kasten braucht die Tastaturbedienung eine eigene Marke — der
    // dickere Strich allein ist als Fokus zu leise.
    ":focus-visible": {
      outlineStyle: "solid",
      outlineWidth: "2px",
      outlineColor: BRAND.purple,
      outlineOffset: "4px",
    },
  },
  // Kompakt: die Breite richtet sich nach der Stellenzahl (siehe `style`), die
  // Zahl steht mittig über dem Strich — so liest sich das Feld als Zähler.
  eingabeKompakt: {
    maxWidth: "none",
    textAlign: "center",
  },
  einheit: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "1rem",
    color: BRAND.muted,
    whiteSpace: "nowrap",
  },
});

type Props = {
  /** Rohwert aus den Antworten — String oder (aus Altbeständen) Zahl. */
  value: unknown;
  onChange: (naechste: string) => void;
  /** Das Feld trägt keine sichtbare Beschriftung, der Name kommt von hier. */
  ariaLabel: string;
  name?: string;
  platzhalter?: string;
  /** Höchstzahl der Zeichen, z. B. 4 für ein Baujahr. */
  maxLen?: number;
  /** Einheit rechts neben der Zahl, z. B. „m²". */
  einheit?: string;
  /** Ein Dezimaltrenner erlaubt (Flächen), sonst nur Ziffern. */
  dezimal?: boolean;
  /** Schmale Variante für Zähler: Breite nach Stellenzahl, Zahl zentriert. */
  kompakt?: boolean;
};

const GrossZahlEingabe = ({
  value,
  onChange,
  ariaLabel,
  name,
  platzhalter,
  maxLen,
  einheit,
  dezimal,
  kompakt,
}: Props) => {
  const styles = useStyles();
  const text = typeof value === "number" ? String(value) : String(value ?? "");
  // Dynamische Breite kann Griffel nicht als Klasse ausdrücken (sie hängt an
  // der Stellenzahl) — deshalb hier als Inline-Maß.
  const breite = kompakt
    ? { width: `${(maxLen ?? 2) + 0.8}ch` }
    : undefined;

  return (
    <span className={styles.zeile}>
      <input
        type="text"
        className={mergeClasses(styles.eingabe, kompakt && styles.eingabeKompakt)}
        style={breite}
        name={name}
        aria-label={ariaLabel}
        inputMode={dezimal ? "decimal" : "numeric"}
        autoComplete="off"
        placeholder={platzhalter}
        value={text}
        onChange={(e) => {
          const naechste = ziffernFilter(e.target.value, { dezimal, maxLen });
          // Nur echte Änderungen wandern nach oben: ein getippter Buchstabe
          // darf im Fenster nicht als ungespeicherte Änderung ankommen.
          if (naechste !== text) onChange(naechste);
        }}
      />
      {einheit && <span className={styles.einheit}>{einheit}</span>}
    </span>
  );
};

export default GrossZahlEingabe;
